import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { useSite } from '@/contexts/SiteContext';
import ProductPicker from '@/components/growth/ProductPicker';
import CategoryPicker from '@/components/growth/CategoryPicker';

interface ThreadData {
  type: string;
  title: string;
  insights: Array<{
    id: string;
    title: string;
    description: string;
    severity?: string;
    impact?: string;
    recommendation?: string;
  }>;
  metrics?: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

const ThreadDetail: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const { selectedSite } = useSite();
  const [threadData, setThreadData] = useState<ThreadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  useEffect(() => {
    // For product threads, show product picker first
    if (type === 'product') {
      setThreadData(null); // Clear any existing data
      setShowProductPicker(true);
      setLoading(false);
      return;
    }

    // For category threads, show category picker first
    if (type === 'category') {
      setThreadData(null); // Clear any existing data
      setShowCategoryPicker(true);
      setLoading(false);
      return; // Don't fetch data yet, wait for category selection
    }

    // Don't fetch if site or type is missing
    if (!selectedSite?._id || !type) return;

    // Don't fetch for product/category types (they have their own pickers)
    if (type === 'product' || type === 'category') return;

    const fetchThreadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use 'homepage' as default entity for non-product threads
        const entity = 'homepage';
        const response = await authFetch(
          `${API_URL}/growth/thread/${type}/${encodeURIComponent(entity)}?siteId=${selectedSite._id}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch thread data: ${response.status}`);
        }

        const data = await response.json();
        setThreadData(data);
      } catch (err) {
        console.error('Error fetching thread data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load thread data');
      } finally {
        setLoading(false);
      }
    };

    fetchThreadData();
  }, [selectedSite?._id, type]);

  // Fetch product thread data when a product is selected
  useEffect(() => {
    if (!selectedProduct || type !== 'product' || !selectedSite?._id) return;

    const fetchProductThread = async () => {
      try {
        setLoading(true);
        setError(null);
        setShowProductPicker(false);

        const response = await authFetch(
          `${API_URL}/growth/thread/product/${encodeURIComponent(selectedProduct.url)}?siteId=${selectedSite._id}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch product thread data: ${response.status}`);
        }

        const data = await response.json();
        setThreadData(data);
      } catch (err) {
        console.error('Error fetching product thread data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load product data');
      } finally {
        setLoading(false);
      }
    };

    fetchProductThread();
  }, [selectedProduct, type, selectedSite?._id]);

  // Fetch category thread data when a category is selected
  useEffect(() => {
    if (!selectedCategory || type !== 'category' || !selectedSite?._id) return;

    const fetchCategoryThread = async () => {
      try {
        setLoading(true);
        setError(null);
        setShowCategoryPicker(false);

        const response = await authFetch(
          `${API_URL}/growth/thread/category/${encodeURIComponent(selectedCategory)}?siteId=${selectedSite._id}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch category thread data: ${response.status}`);
        }

        const data = await response.json();
        setThreadData(data);
      } catch (err) {
        console.error('Error fetching category thread data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load category data');
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryThread();
  }, [selectedCategory, type, selectedSite?._id]);

  const getThreadTitle = (threadType: string | undefined) => {
    const titles: Record<string, string> = {
      category: 'Category Analysis',
      product: 'Product Insights',
      page: 'Page Performance',
      friction: 'Friction Points',
      performance: 'Performance Issues',
      segment: 'Customer Segments',
      intent: 'User Intent Analysis'
    };
    return titles[threadType || ''] || 'Thread Analysis';
  };

  if (loading && !showProductPicker && !showCategoryPicker) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error Loading Thread</h3>
          <p className="text-red-600 mt-1">{error}</p>
          <button
            onClick={() => navigate('/growth')}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Growth Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/growth')}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Growth Dashboard
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">
            {getThreadTitle(type)} {type === 'category' && !selectedCategory && '- Select a Category'}
          </h1>
          {selectedSite && (
            <p className="text-gray-600 mt-1">
              Analysis for {selectedSite.name || selectedSite.url}
            </p>
          )}
          {type === 'product' && selectedProduct && (
            <div className="mt-3 flex items-center justify-between">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-600">Selected Product: </span>
                <span className="font-medium text-gray-900">{selectedProduct.name}</span>
              </div>
              <button
                onClick={() => {
                  setSelectedProduct(null);
                  setThreadData(null);
                  setShowProductPicker(true);
                }}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Change Product
              </button>
            </div>
          )}
          {type === 'category' && selectedCategory && (
            <div className="mt-3 flex items-center justify-between">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-600">Selected Category: </span>
                <span className="font-medium text-gray-900">{selectedCategory}</span>
              </div>
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setThreadData(null);
                  setShowCategoryPicker(true);
                }}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Change Category
              </button>
            </div>
          )}
        </div>

        <div className="p-6">
          {/* Category Picker for category threads */}
          {type === 'category' && showCategoryPicker && selectedSite?._id && (
            <CategoryPicker
              siteId={selectedSite._id}
              onCategorySelect={(category) => {
                setSelectedCategory(category);
                setShowCategoryPicker(false);
              }}
              selectedCategory={selectedCategory}
            />
          )}
          {/* Product Picker for product threads */}
          {type === 'product' && showProductPicker && selectedSite?._id && (
            <ProductPicker
              siteId={selectedSite._id}
              onProductSelect={(product) => {
                setSelectedProduct(product);
                setShowProductPicker(false);
              }}
              selectedProduct={selectedProduct}
            />
          )}
          {!showProductPicker && !showCategoryPicker && threadData ? (
            <div>
              {/* Metrics Summary */}
              {threadData.metrics && (
                <div className="grid grid-cols-5 gap-4 mb-8">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {threadData.metrics.total}
                    </div>
                    <div className="text-sm text-gray-600">Total Issues</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-600">
                      {threadData.metrics.critical}
                    </div>
                    <div className="text-sm text-gray-600">Critical</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-orange-600">
                      {threadData.metrics.high}
                    </div>
                    <div className="text-sm text-gray-600">High</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">
                      {threadData.metrics.medium}
                    </div>
                    <div className="text-sm text-gray-600">Medium</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {threadData.metrics.low}
                    </div>
                    <div className="text-sm text-gray-600">Low</div>
                  </div>
                </div>
              )}

              {/* Insights List */}
              <div className="space-y-4">
                {threadData.insights && threadData.insights.length > 0 ? (
                  threadData.insights.map((insight) => (
                    <div
                      key={insight.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {insight.title}
                      </h3>
                      <p className="text-gray-600 mb-3">{insight.description}</p>

                      {insight.severity && (
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full
                          ${insight.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            insight.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                            insight.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'}`}>
                          {insight.severity}
                        </span>
                      )}

                      {insight.recommendation && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-md">
                          <div className="text-sm font-medium text-blue-900">
                            Recommendation:
                          </div>
                          <div className="text-sm text-blue-700 mt-1">
                            {insight.recommendation}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p>No insights available for this thread type yet.</p>
                    <p className="mt-2 text-sm">Check back later for detailed analysis.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            !showProductPicker && !showCategoryPicker && (
              <div className="text-center py-12 text-gray-500">
                <p>No data available for this thread.</p>
                <button
                  onClick={() => navigate('/growth')}
                  className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                >
                  ← Back to Growth Dashboard
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ThreadDetail;